/* =========================
   Question & Option Types
   ========================= */

export interface QuestionOption {
  option_id: number;
  option_text: string | null;
  option_url: string | null;
}

export enum AnswerType {
  MCQ = "MCQ",
  TRUE_FALSE = "TRUE_FALSE",
  DRAG_DROP = "DRAG_DROP",
}

export interface DragDropItem {
  id: number;
  item_text: string | null;
  item_url: string | null;
  target_bucket?: string; // Correct bucket for evaluation/review
}

export interface Question {
  question_id: number;
  question_type_id: number;
  question_type_name: string;

  question_text: string;
  question_url: string | null;
  prompt: string | null;
  prompt_url: string | null; // Image prompt URL
  instruction: string | null;
  imgWidth: number;
  prompt_imgWidth: number;

  source_type: string;

  subtype_id: number | null;
  subtype_name: string | null;

  options: QuestionOption[];
  answer_type: AnswerType;
  drag_drop_data?: DragDropItem[];
  drag_drop_pattern?: "items_to_buckets" | "buckets_to_items"; // items_to_buckets: drag items to fixed buckets, buckets_to_items: drag buckets to fixed items

  // Review / result mode only
  correctAnswerIndex?: number;
  explanation?: string;
  displayIndex?: number;
}

/* =========================
   Exam Structure Types
   ========================= */

export interface ExamSection {
  id: number;                // question_type_id
  title: string;             // question_type_name
  questions: Question[];
}

export interface ExamData {
  title: string;
  sections: ExamSection[];
}

/* =========================
   Exam Flow & State
   ========================= */

export enum ExamPhase {
  START = "START",
  INSTRUCTIONS = "INSTRUCTIONS",
  IN_PROGRESS = "IN_PROGRESS",
  COMPLETED = "COMPLETED",
  REVIEW = "REVIEW",
}

/* =========================
   User Answer & Reducer State
   ========================= */

export interface UserAnswer {
  selectedOptionIndex: number | null; // null if skipped, used for MCQ/TRUE_FALSE
  dragDropAnswers?: Record<number, string | null>; // For DRAG_DROP questions: itemId -> bucketName
  sentence?: string | null;
  answer_data?: string | null;
  isFlagged: boolean;
  isViewed?: boolean;
}

export interface ExamState {
  phase: ExamPhase;
  currentSectionIndex: number;
  currentQuestionIndex: number;
  answers: Record<number, UserAnswer>; // key = question_id
  timeRemaining: number;
  isCalculatorOpen: boolean;
  isNavigatorOpen: boolean;
}

/* =========================
   Reducer Actions
   ========================= */

export type ExamAction =
  | { type: "START_SECTION"; time: number; index?: number }
  | { type: "NEXT_QUESTION" }
  | { type: "PREV_QUESTION" }
  | { type: "SELECT_ANSWER"; questionId: number; optionIndex: number }
  | { type: "SET_DRAG_DROP_ANSWER"; questionId: number; itemId: number; bucketName: string | null; isExclusive?: boolean }
  | { type: "TOGGLE_FLAG"; questionId: number }
  | { type: "TICK_TIMER" }
  | { type: "TOGGLE_CALCULATOR" }
  | { type: "TOGGLE_NAVIGATOR" }
  | { type: "JUMP_QUESTION"; index: number }
  | { type: "VIEW_QUESTION"; questionId: number }
  | { type: "LOAD_EXISTING_ANSWERS"; answers: Record<number, UserAnswer> }
  | { type: "SYNC_FLAG"; questionId: number; isFlagged: boolean };
